"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ShieldCheck, Layers, FlaskConical, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface SolutionItem {
  id: string;
  title: string;
  description: string;
  href: string;
  iconName: "activity" | "shield" | "layers" | "flask";
}
const solutions: SolutionItem[] = [{
  id: "pat",
  title: "Process Analytical Technology",
  description: "In-line and on-line spectroscopic tools for real-time process monitoring and quality control.",
  href: "/ProcessAnalyticalTechnologyPage",
  iconName: "activity"
}, {
  id: "safety-security",
  title: "Safety and Security",
  description: "Handheld Raman and NIR analyzers for rapid raw material verification and hazardous threat detection.",
  href: "/SafetyAndSecurityPage",
  iconName: "shield"
}, {
  id: "powder-testing",
  title: "Powder Testing",
  description: "Pharmacopoeial physical test instruments for powder flow, bulk density, and compressibility analysis.",
  href: "/PowderTestingPage",
  iconName: "layers"
}, {
  id: "chromatography",
  title: "Chromatography",
  description: "Advanced HPLC solvent recovery systems and consumables designed to lower laboratory operating costs.",
  href: "/ChromatographyPage",
  iconName: "flask"
}];
function renderSolutionIcon(name: SolutionItem["iconName"]) {
  switch (name) {
    case "activity":
      return <Activity className="size-6 text-primary" aria-hidden="true" />;
    case "shield":
      return <ShieldCheck className="size-6 text-primary" aria-hidden="true" />;
    case "layers":
      return <Layers className="size-6 text-primary" aria-hidden="true" />;
    case "flask":
      return <FlaskConical className="size-6 text-primary" aria-hidden="true" />;
    default:
      return <Activity className="size-6 text-primary" aria-hidden="true" />;
  }
}
export default function RelatedSolutions() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="relatedsolutions" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="mb-12 max-w-2xl">
          <p className="text-sm font-semibold tracking-wider uppercase text-primary mb-2">
            Explore Capabilities
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground">
            Related Solution Hubs
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            Targeted analytical instrumentation, qualification protocols, and lifecycle support across core application sectors.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {solutions.map((item, index) => <motion.div key={item.id} data-index={index} initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : index * 0.08
        }} className="flex flex-col h-full">
              <Card className="flex flex-col justify-between h-full border border-border bg-card hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <CardHeader className="space-y-4">
                  <div className="flex size-12 items-center justify-center rounded-lg bg-secondary border border-border/50">
                    {renderSolutionIcon(item.iconName)}
                  </div>
                  <CardTitle className="text-lg font-semibold text-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-1">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-4">
                  <Button asChild variant="ghost" className="w-full justify-between px-0 font-medium text-foreground hover:text-primary hover:bg-transparent group">
                    <Link to={item.href} className="flex items-center justify-between w-full">
                      <span>Explore Solution</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 text-primary" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}